import * as React from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useCameraList } from '@cguard/api-client/hooks/cameras';
import { useCreateCameraPlayback } from '@cguard/api-client/hooks/video-console';
import type { CameraId } from '@cguard/types';
import { DateTimePicker } from '@cguard/design-system';
import { CalendarClock, Pause, Play, Radio, RotateCcw, SkipBack, SkipForward } from 'lucide-react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { recordingCoverage, stopCameraPlayback } from '@cguard/api-client/hooks/evidence-exports';
import { MultiPlayback } from '~/components/multi-playback';
import { CameraRail } from '~/components/camera-rail';
import { readActionError } from '~/lib/action-error';
import { resolveCameraSelection } from '~/lib/camera-navigation';
import { withoutSeededDemoCameras } from '~/lib/production-roster';
import { formatSiteInputTime, siteInputToDate, toSiteInputValue } from '~/lib/site-time';

const MAX_PLAYBACK_MINUTES = 30;
const SEEK_STEP_SECONDS = 5;
const SEEK_JUMP_SECONDS = 30;

export type PlaybackRangeProblem = 'incomplete' | 'end-before-start' | 'too-long';

interface LoadedRange {
  readonly from: string;
  readonly to: string;
  readonly minutes: number;
}

export function PlaybackPage({
  cameraId,
}: {
  readonly cameraId?: string;
} = {}): React.JSX.Element {
  const { t } = useTranslation('video');
  const navigate = useNavigate();
  const cameraQuery = useCameraList();
  const cameras = withoutSeededDemoCameras(cameraQuery.data ?? []);
  const selectedId = resolveCameraSelection(cameras, cameraId);
  const selected = cameras.find((camera) => camera.id === selectedId) ?? null;
  const hasPlaybackSource = selected?.playbackConfigured ?? false;
  const initialRange = React.useMemo(() => defaultPlaybackRange(new Date()), []);
  const [from, setFrom] = React.useState(initialRange.from);
  const [to, setTo] = React.useState(initialRange.to);
  const [loadedRange, setLoadedRange] = React.useState<LoadedRange | null>(null);
  const [playing, setPlaying] = React.useState(false);
  const [currentTime, setCurrentTime] = React.useState(0);
  const [duration, setDuration] = React.useState(0);
  const playback = useCreateCameraPlayback();
  const [session, setSession] = React.useState<Awaited<
    ReturnType<typeof playback.mutateAsync>
  > | null>(null);
  const [multi, setMulti] = React.useState(false);
  const [coverageError, setCoverageError] = React.useState('');
  const [checkingCoverage, setCheckingCoverage] = React.useState(false);
  const generation = React.useRef(0);
  const activeUrl = React.useRef<string | null>(null);
  React.useEffect(() => {
    generation.current++;
    if (activeUrl.current) void stopCameraPlayback(activeUrl.current).catch(() => undefined);
    activeUrl.current = null;
    playback.reset();
    setSession(null);
    setCheckingCoverage(false);
    setCoverageError('');
    return () => {
      generation.current++;
      if (activeUrl.current) void stopCameraPlayback(activeUrl.current).catch(() => undefined);
      activeUrl.current = null;
    };
  }, [selectedId, from, to, multi, playback.reset]);
  const resetPlayback = playback.reset;
  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const shownCameraId = React.useRef(selectedId);
  const fromId = React.useId();
  const toId = React.useId();
  const isFixturePlayback = session?.url.includes('/v1/mock-evidence/') ?? false;
  const exportFrom = siteInputToDate(from)?.toISOString();
  const exportTo = siteInputToDate(to)?.toISOString();
  const rangeMinutes = playbackRangeMinutes(from, to);
  const rangeProblem = playbackRangeProblem(from, to);
  const rangeChanged =
    session !== null &&
    loadedRange !== null &&
    (loadedRange.from !== from || loadedRange.to !== to);
  const progressPercent =
    duration > 0 ? Math.min(100, Math.max(0, (currentTime / duration) * 100)) : 0;
  const timelineStyle = {
    '--timeline-progress': `${progressPercent}%`,
  } as React.CSSProperties;

  // A different camera starts from an empty player; the chosen range is kept.
  React.useEffect(() => {
    if (shownCameraId.current === selectedId) return;
    shownCameraId.current = selectedId;
    resetPlayback();
    setLoadedRange(null);
    setPlaying(false);
    setCurrentTime(0);
    setDuration(0);
  }, [resetPlayback, selectedId]);

  const selectCamera = (nextCameraId: CameraId): void => {
    void navigate({ to: '/playback', search: { cameraId: nextCameraId }, replace: true });
  };

  const loadPlayback = (): void => {
    const start = siteInputToDate(from);
    const end = siteInputToDate(to);
    if (!selectedId || rangeProblem || !start || !end) return;
    const run = ++generation.current;
    const requested: LoadedRange = { from, to, minutes: rangeMinutes };
    setCoverageError('');
    setCheckingCoverage(true);
    void (async () => {
      try {
        if (activeUrl.current) await stopCameraPlayback(activeUrl.current);
        activeUrl.current = null;
        playback.reset();
        setSession(null);
        const coverage = await recordingCoverage(
          selectedId,
          start.toISOString(),
          end.toISOString(),
        );
        if (!coverage.supported) throw new Error(t('exports.unsupported'));
        if (coverage.gaps.length) throw new Error(t('exports.gaps'));
        if (run !== generation.current) return;
        const response = await playback.mutateAsync({
          cameraId: selectedId,
          from: start.toISOString(),
          to: end.toISOString(),
        });
        if (run !== generation.current) {
          await stopCameraPlayback(response.url);
          return;
        }
        activeUrl.current = response.url;
        setSession(response);
        setLoadedRange(requested);
        setPlaying(false);
        setCurrentTime(0);
        setDuration(0);
      } catch (error) {
        if (run === generation.current)
          setCoverageError(readActionError(error, t('playback.loadError')));
      } finally {
        if (run === generation.current) setCheckingCoverage(false);
      }
    })();
  };

  const keepLastAllowedMinutes = (): void => {
    const end = siteInputToDate(to);
    if (!end) return;
    setFrom(toSiteInputValue(new Date(end.getTime() - MAX_PLAYBACK_MINUTES * 60_000)));
  };

  const togglePlayback = (): void => {
    if (isFixturePlayback) {
      setPlaying((current) => !current);
      return;
    }
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      if (video.ended) video.currentTime = 0;
      void video
        .play()
        .then(() => setPlaying(true))
        .catch(() => setPlaying(false));
    } else {
      video.pause();
      setPlaying(false);
    }
  };

  React.useEffect(() => {
    if (!isFixturePlayback || !playing || duration <= 0) return;
    const timer = window.setInterval(() => {
      setCurrentTime((current) => {
        const next = Math.min(duration, current + 1);
        if (next >= duration) setPlaying(false);
        return next;
      });
    }, 1000);
    return () => window.clearInterval(timer);
  }, [duration, isFixturePlayback, playing]);

  // Keyboard transport: Space plays or pauses, arrows step 5 s (30 s with Shift).
  // Ignored while a control has focus so typing and native control keys keep working.
  React.useEffect(() => {
    if (!session) return;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;
      if (
        event.target instanceof Element &&
        event.target.closest('input, select, textarea, button, a, [contenteditable="true"]')
      ) {
        return;
      }
      if (event.key === ' ') {
        event.preventDefault();
        togglePlayback();
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        const step = event.shiftKey ? SEEK_JUMP_SECONDS : SEEK_STEP_SECONDS;
        seek(event.key === 'ArrowLeft' ? -step : step);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  });

  const rangeMessage = rangeProblem ? describeRangeProblem(rangeProblem, rangeMinutes, t) : null;
  const shownRange = loadedRange ?? { from, to, minutes: rangeMinutes };

  return (
    <div className="video-workspace">
      <CameraRail
        siteFirst
        cameras={cameras}
        selectedId={selectedId}
        onSelect={selectCamera}
        annotate={(camera) => (camera.playbackConfigured ? null : t('playback.noRecordings'))}
      />
      <section className="viewer-column playback-column">
        <div className="viewer-titlebar">
          <div className="min-w-0">
            <p className="eyebrow">{t('nav.playback')}</p>
            <h1>{selected?.name ?? t('viewer.selectCamera')}</h1>
          </div>
          <div className="titlebar-actions">
            <button className="secondary-button" onClick={() => setMulti((value) => !value)}>
              {t(multi ? 'exports.single' : 'exports.multi')}
            </button>
            {selected && (
              <Link
                className="secondary-button"
                to="/exports"
                search={{
                  cameraId: selected.id,
                  ...(exportFrom ? { from: exportFrom } : {}),
                  ...(exportTo ? { to: exportTo } : {}),
                }}
              >
                {t('exports.create')}
              </Link>
            )}
            {hasPlaybackSource ? (
              <span className={`range-readout ${rangeProblem ? 'is-warning' : ''}`}>
                {rangeProblem === 'too-long'
                  ? t('playback.readout.tooLong', {
                      length: formatRangeLength(rangeMinutes, t),
                      max: MAX_PLAYBACK_MINUTES,
                    })
                  : rangeProblem
                    ? t('playback.readout.invalid')
                    : t('playback.readout.minutes', { minutes: rangeMinutes })}
              </span>
            ) : null}
            {selected ? (
              <Link to="/live" search={{ cameraId: selected.id }} className="secondary-button">
                <Radio aria-hidden size={14} />
                {t('viewer.viewLive')}
              </Link>
            ) : null}
          </div>
        </div>

        {multi && !rangeProblem ? (
          <MultiPlayback
            from={siteInputToDate(from)!.toISOString()}
            to={siteInputToDate(to)!.toISOString()}
          />
        ) : (
          <div
            className={`viewer-stage playback-stage ${
              isFixturePlayback ? 'fixture-playback-frame' : ''
            }`}
          >
            {cameraQuery.isLoading ? (
              <div
                className="viewer-message"
                role="status"
                data-cguard-state="loading"
                data-cguard-surface="playback"
              >
                <RotateCcw className="animate-spin" aria-hidden size={24} />
                <h3>{t('viewer.loadingCameras')}</h3>
                <p>{t('playback.loading.detail')}</p>
              </div>
            ) : cameraQuery.isError ? (
              <div
                className="viewer-message"
                role="alert"
                data-cguard-state="error"
                data-cguard-surface="playback"
              >
                <CalendarClock aria-hidden size={30} strokeWidth={1.4} />
                <h3>{t('playback.error.title')}</h3>
                <p>{t('playback.error.detail')}</p>
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => void cameraQuery.refetch()}
                >
                  <RotateCcw aria-hidden size={14} />
                  {t('common:actions.retry')}
                </button>
              </div>
            ) : cameras.length === 0 ? (
              <div
                className="viewer-message"
                data-cguard-state="ready"
                data-cguard-surface="playback"
              >
                <CalendarClock aria-hidden size={30} strokeWidth={1.4} />
                <h3>{t('playback.empty.title')}</h3>
                <p>{t('playback.empty.detail')}</p>
              </div>
            ) : session && isFixturePlayback ? (
              <div
                className="fixture-static-player"
                role="img"
                aria-label={t('playback.fixtureRecording', {
                  name: selected?.name ?? t('playback.cameraFallback'),
                })}
                data-testid="fixture-playback-player"
              />
            ) : session ? (
              <video
                ref={videoRef}
                key={session.url}
                src={session.url}
                className="h-full w-full object-contain"
                playsInline
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
                onEnded={() => setPlaying(false)}
                onTimeUpdate={(event) => setCurrentTime(event.currentTarget.currentTime)}
                onLoadedMetadata={(event) =>
                  setDuration(
                    resolvePlaybackDuration(event.currentTarget.duration, shownRange.minutes),
                  )
                }
                onDurationChange={(event) =>
                  setDuration(
                    resolvePlaybackDuration(event.currentTarget.duration, shownRange.minutes),
                  )
                }
              />
            ) : (
              <div
                className="viewer-message"
                data-cguard-state="ready"
                data-cguard-surface="playback"
              >
                <CalendarClock aria-hidden size={30} strokeWidth={1.4} />
                <h3>
                  {!hasPlaybackSource
                    ? t('playback.state.unavailable.title')
                    : rangeMessage
                      ? t('playback.state.adjust.title')
                      : t('playback.state.ready.title')}
                </h3>
                <p>
                  {!hasPlaybackSource
                    ? t('playback.state.unavailable.detail')
                    : rangeMessage
                      ? t('playback.state.adjust.detail')
                      : t('playback.state.ready.detail')}
                </p>
              </div>
            )}
            {playback.isError ? (
              <div className="playback-error" role="alert">
                <span>{readActionError(playback.error, t('playback.loadError'))}</span>
                <button
                  type="button"
                  className="text-button"
                  disabled={rangeProblem !== null}
                  onClick={loadPlayback}
                >
                  {t('common:actions.retry')}
                </button>
              </div>
            ) : null}
          </div>
        )}
        {coverageError && <p role="alert">{coverageError}</p>}

        <div className="time-dock">
          <div className="range-controls">
            <DateTimePicker
              id={fromId}
              label={t('playback.from')}
              className={`range-field ${rangeProblem ? 'is-invalid' : ''}`}
              value={from}
              max={to}
              disabled={!hasPlaybackSource}
              onChange={setFrom}
            />
            <span className="range-arrow">→</span>
            <DateTimePicker
              id={toId}
              label={t('playback.to')}
              className={`range-field ${rangeProblem ? 'is-invalid' : ''}`}
              value={to}
              min={from}
              max={toSiteInputValue(new Date())}
              disabled={!hasPlaybackSource}
              onChange={setTo}
            />
            <button
              type="button"
              className="primary-button"
              disabled={
                !hasPlaybackSource ||
                rangeProblem !== null ||
                playback.isPending ||
                checkingCoverage ||
                multi
              }
              onClick={loadPlayback}
            >
              {playback.isPending ? (
                <RotateCcw className="animate-spin" aria-hidden size={14} />
              ) : null}
              {playback.isPending
                ? t('playback.loading.button')
                : rangeChanged
                  ? t('playback.reload')
                  : t('playback.load')}
            </button>
            {hasPlaybackSource && rangeMessage ? (
              <p className="range-error">
                {rangeMessage}
                {rangeProblem === 'too-long' ? (
                  <button type="button" className="text-button" onClick={keepLastAllowedMinutes}>
                    {t('playback.useLast', { max: MAX_PLAYBACK_MINUTES })}
                  </button>
                ) : null}
              </p>
            ) : rangeChanged ? (
              <p className="range-hint">{t('playback.rangeChanged')}</p>
            ) : null}
          </div>

          <div className="playback-transport">
            <div className="transport-controls">
              <button
                type="button"
                aria-label={t('playback.back')}
                onClick={() => seek(-10)}
                disabled={!session}
              >
                <SkipBack aria-hidden size={17} />
              </button>
              <button
                type="button"
                className="transport-play"
                aria-label={playing ? t('playback.pause') : t('playback.play')}
                onClick={togglePlayback}
                disabled={!session}
              >
                {playing ? <Pause aria-hidden size={18} /> : <Play aria-hidden size={18} />}
              </button>
              <button
                type="button"
                aria-label={t('playback.forward')}
                onClick={() => seek(10)}
                disabled={!session}
              >
                <SkipForward aria-hidden size={17} />
              </button>
            </div>

            <div className="timeline">
              <span className="timeline-track" style={timelineStyle}>
                <input
                  className="timeline-scrubber"
                  type="range"
                  min="0"
                  max={duration > 0 ? duration : 1}
                  step="1"
                  value={duration > 0 ? Math.min(currentTime, duration) : 0}
                  disabled={!session || duration <= 0}
                  aria-label={t('playback.position')}
                  aria-valuetext={formatElapsed(currentTime)}
                  onChange={(event) => seekTo(Number(event.target.value))}
                />
              </span>
              <span className="timeline-labels">
                <span>{t('playback.start', { time: formatSiteInputTime(shownRange.from) })}</span>
                <span>
                  {session
                    ? `${formatElapsed(currentTime)} / ${formatElapsed(duration)}`
                    : t('playback.loadToBegin')}
                </span>
                <span>{t('playback.end', { time: formatSiteInputTime(shownRange.to) })}</span>
              </span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );

  function seek(seconds: number): void {
    if (videoRef.current) {
      seekTo(videoRef.current.currentTime + seconds);
    } else if (isFixturePlayback) {
      seekTo(currentTime + seconds);
    }
  }

  function seekTo(seconds: number): void {
    if ((!videoRef.current && !isFixturePlayback) || !Number.isFinite(seconds)) return;
    const upperBound = duration > 0 ? duration : Number.MAX_SAFE_INTEGER;
    const next = Math.min(upperBound, Math.max(0, seconds));
    if (videoRef.current) videoRef.current.currentTime = next;
    setCurrentTime(next);
  }
}

export function defaultPlaybackRange(now: Date): { from: string; to: string } {
  return {
    from: toSiteInputValue(new Date(now.getTime() - 10 * 60_000)),
    to: toSiteInputValue(now),
  };
}

export function playbackRangeMinutes(from: string, to: string): number {
  const start = siteInputToDate(from);
  const end = siteInputToDate(to);
  if (!start || !end) return 0;
  return Math.round((end.getTime() - start.getTime()) / 60_000);
}

export function playbackRangeProblem(from: string, to: string): PlaybackRangeProblem | null {
  if (!siteInputToDate(from) || !siteInputToDate(to)) return 'incomplete';
  const minutes = playbackRangeMinutes(from, to);
  if (minutes <= 0) return 'end-before-start';
  if (minutes > MAX_PLAYBACK_MINUTES) return 'too-long';
  return null;
}

function describeRangeProblem(
  problem: PlaybackRangeProblem,
  minutes: number,
  t: TFunction<'video'>,
): string {
  switch (problem) {
    case 'incomplete':
      return t('playback.problem.incomplete');
    case 'end-before-start':
      return t('playback.problem.endBeforeStart');
    default:
      return t('playback.problem.tooLong', {
        max: MAX_PLAYBACK_MINUTES,
        length: formatRangeLength(minutes, t),
      });
  }
}

function formatRangeLength(minutes: number, t: TFunction<'video'>): string {
  if (minutes < 60) return t('playback.length.minutes', { minutes });
  const hours = Math.floor(minutes / 60);
  if (minutes % 60 === 0) return t('playback.length.wholeHours', { hours });
  return t('playback.length.hours', { hours, minutes: minutes % 60 });
}

function formatElapsed(value: number): string {
  if (!Number.isFinite(value) || value < 0) return '00:00';
  const wholeSeconds = Math.floor(value);
  const minutes = Math.floor(wholeSeconds / 60);
  const seconds = wholeSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

function resolvePlaybackDuration(mediaDuration: number, rangeMinutes: number): number {
  if (Number.isFinite(mediaDuration) && mediaDuration > 0) return mediaDuration;
  return rangeMinutes > 0 ? rangeMinutes * 60 : 0;
}
